import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/Button';

interface TokenEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (token: string) => Promise<void>;
  visitNumber: string;
  isLoading?: boolean;
  error?: string | null;
}

export const TokenEntryModal: React.FC<TokenEntryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  visitNumber,
  isLoading = false,
  error = null,
}) => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '']);
  const [countdown, setCountdown] = useState<number>(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setDigits(['', '', '', '']);
      setIsSuccess(false);
      setCountdown(60);
      // Focus first input after a short delay
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, countdown]);

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit numbers
    if (value.length > 1) {
      value = value.slice(-1);
    }

    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length === 4) {
      const newDigits = pastedData.split('');
      setDigits(newDigits);
      inputRefs.current[3]?.focus();
    }
  };

  const handleSubmit = useCallback(async () => {
    const token = digits.join('');
    if (token.length !== 4) return;

    try {
      await onSubmit(token);
      setIsSuccess(true);
      // Close modal after success animation
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch {
      // Error is handled by parent component via error prop
    }
  }, [digits, onSubmit, onClose]);

  const isTokenComplete = digits.every((d) => d !== '');

  // Handle Enter key to submit
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isTokenComplete && !isLoading && isOpen) {
        handleSubmit();
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [isTokenComplete, isLoading, isOpen, handleSubmit]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-full max-w-md bg-white rounded-2xl shadow-xl transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Success State */}
          {isSuccess ? (
            <div className="p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                Visit Completed!
              </h3>
              <p className="text-secondary-600">
                The visit has been marked as completed successfully.
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-6 py-5 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900">
                      Enter Closure Token
                    </h3>
                    <p className="text-sm text-secondary-500 mt-1">
                      Visit #{visitNumber}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
                    aria-label="Close modal"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-6">
                <p className="text-sm text-secondary-600 text-center mb-6">
                  Enter the 4-digit token provided by the developer to verify the visit completion.
                </p>

                {/* Token Input */}
                <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
                  {digits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleDigitChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={`
                        w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 transition-all
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                        ${error ? 'border-red-300 bg-red-50' : 'border-secondary-300 bg-white'}
                        ${digit ? 'border-primary-400' : ''}
                      `}
                      disabled={isLoading}
                      aria-label={`Digit ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Countdown Timer */}
                {countdown > 0 && (
                  <div className="text-center mb-4">
                    <p className="text-sm text-secondary-500">
                      Token expires in{' '}
                      <span className="font-medium text-secondary-700">
                        {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                      </span>
                    </p>
                  </div>
                )}

                {countdown === 0 && (
                  <div className="text-center mb-4">
                    <p className="text-sm text-orange-600">
                      Token may have expired. Please request a new token from the developer.
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-red-500 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Cost Warning */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-amber-800">QLead Cost Deduction</p>
                      <p className="text-sm text-amber-700 mt-0.5">
                        Completing this visit will deduct Rs. 5 from your wallet for QLead verification.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-secondary-50 border-t border-secondary-200 rounded-b-2xl flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isTokenComplete || isLoading}
                  isLoading={isLoading}
                  fullWidth
                >
                  Complete Visit
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TokenEntryModal;
